import { useEffect, useMemo } from 'react';
import { Activity, AlertTriangle, CheckCircle2, PauseCircle, RefreshCw, Repeat, Timer, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { PanelSkeleton, Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useChartColors } from '../hooks/useChartColors';
import { useSystemHealthStore } from '../store/systemHealthStore';
import { ChartPanel } from '../components/ChartPanel';
import { DeadLetterTable } from '../components/DeadLetterTable';
import { DocumentCaptureHealthPanel } from '../components/DocumentCaptureHealthPanel';
import { GatePanel } from '../components/GatePanel';
import { OutboxLiveTiles } from '../components/OutboxLiveTiles';
import { SeriesTable } from '../components/SeriesTable';
import { StatKpiCard } from '../components/StatKpiCard';
import { HorizontalBarList } from '../components/charts/HorizontalBarList';
import { StackedBarTrend } from '../components/charts/StackedBarTrend';
import type { TimeRange } from '../types/systemHealth.types';

const LIVE_POLL_MS = 8000;
const RANGES: TimeRange[] = ['24h', '7d', '30d', '90d'];
const HANDLER_LABEL: Record<string, string> = {
  'posting.mis': 'MIS post',
  'posting.ir': 'IR post',
  'posting.so': 'SO post',
  'posting.cl': 'CL post',
  'mrs.status': 'MRS status',
  'webhook.refresh': 'Cache refresh',
};

const fmtDate = (s: string) => new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtHour = (s: string) => `${String(new Date(s).getHours()).padStart(2, '0')}:00`;
const handlerLabel = (t: string) => HANDLER_LABEL[t] ?? t;

/** Section shell — uppercase muted label + a one-line scope caption, then content. */
function Section({ label, caption, children }: { label: string; caption: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h2>
        <p className="text-xs text-muted-foreground">{caption}</p>
      </div>
      {children}
    </section>
  );
}

export function SystemHealthPage() {
  usePageTitle('System Health');
  const c = useChartColors();

  const range = useSystemHealthStore((s) => s.range);
  const outboxTrends = useSystemHealthStore((s) => s.outboxTrends);
  const netsuiteTrends = useSystemHealthStore((s) => s.netsuiteTrends);
  const httpTrends = useSystemHealthStore((s) => s.httpTrends);
  const isLoadingTrends = useSystemHealthStore((s) => s.isLoadingTrends);
  const trendsError = useSystemHealthStore((s) => s.trendsError);
  const lastRefresh = useSystemHealthStore((s) => s.lastRefresh);
  const fetchTrends = useSystemHealthStore((s) => s.fetchTrends);
  const fetchLive = useSystemHealthStore((s) => s.fetchLive);
  const fetchDocScanHealth = useSystemHealthStore((s) => s.fetchDocScanHealth);
  const setRange = useSystemHealthStore((s) => s.setRange);
  const refresh = useSystemHealthStore((s) => s.refresh);
  const clearError = useSystemHealthStore((s) => s.clearError);

  // Initial load + live-tile poll (independent of the SSE gate stream and the trend charts). The Document
  // Capture aggregate rides the same low-frequency cadence (its own store slice → only its panel re-renders).
  useEffect(() => {
    fetchTrends();
    fetchLive();
    fetchDocScanHealth();
    const id = setInterval(() => {
      fetchLive();
      fetchDocScanHealth();
    }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchTrends, fetchLive, fetchDocScanHealth]);

  // ---- HTTP hourly → display buckets (per-hour at 24h, rolled to per-day beyond). Both branches emit the
  // SAME row shape so the chart/table generics infer cleanly. ----
  const httpBuckets = useMemo(() => {
    const rows = httpTrends?.perHour ?? [];
    const shape = (label: string, total: number, c2: number, c3: number, c4: number, c5: number, avg: number, max: number) => ({
      label, total, count2xx: c2, count3xx: c3, count4xx: c4, count5xx: c5, avgDurationMs: avg, maxDurationMs: max,
    });
    if (range === '24h') {
      return rows.map((r) => shape(fmtHour(r.hourUtc), r.total, r.count2xx, r.count3xx, r.count4xx, r.count5xx, r.avgDurationMs, r.maxDurationMs));
    }
    const byDay = new Map<string, { label: string; total: number; c2: number; c3: number; c4: number; c5: number; durSum: number; max: number }>();
    for (const r of rows) {
      const d = new Date(r.hourUtc);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const b = byDay.get(key) ?? { label: fmtDate(r.hourUtc), total: 0, c2: 0, c3: 0, c4: 0, c5: 0, durSum: 0, max: 0 };
      b.total += r.total; b.c2 += r.count2xx; b.c3 += r.count3xx; b.c4 += r.count4xx; b.c5 += r.count5xx;
      b.durSum += r.avgDurationMs * r.total; b.max = Math.max(b.max, r.maxDurationMs);
      byDay.set(key, b);
    }
    return [...byDay.values()].map((b) => shape(b.label, b.total, b.c2, b.c3, b.c4, b.c5, b.total ? b.durSum / b.total : 0, b.max));
  }, [httpTrends, range]);

  const httpTotals = useMemo(() => {
    const rows = httpTrends?.perHour ?? [];
    const total = rows.reduce((n, r) => n + r.total, 0);
    const err5 = rows.reduce((n, r) => n + r.count5xx, 0);
    const durSum = rows.reduce((n, r) => n + r.avgDurationMs * r.total, 0);
    return { total, errorRatePct: total ? (err5 / total) * 100 : 0, avgMs: total ? durSum / total : 0 };
  }, [httpTrends]);

  // ---- Derived series data (compute failed/rejects aggregates the stacks need). ----
  const rejectRows = useMemo(
    () => (netsuiteTrends?.callsPerDay ?? []).map((d) => ({ ...d, rejects: d.count429 + d.count503 })),
    [netsuiteTrends],
  );
  const attemptRows = useMemo(
    () => (outboxTrends?.attemptsPerDay ?? []).map((d) => ({ ...d, failed: d.failedTransient + d.failedPermanent })),
    [outboxTrends],
  );
  const handlerRows = useMemo(
    () => (outboxTrends?.postsByHandler ?? []).map((h) => ({ ...h, label: handlerLabel(h.type) })),
    [outboxTrends],
  );
  const deferredTotal = useMemo(
    () => (outboxTrends?.attemptsPerDay ?? []).reduce((n, d) => n + d.deferred, 0),
    [outboxTrends],
  );

  const rangeCaption = range === '24h' ? 'last 24 hours' : range === '7d' ? 'last 7 days' : range === '30d' ? 'last 30 days' : 'last 90 days';

  // ---- Loading / error (initial) ----
  if (isLoadingTrends && !outboxTrends) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-6">
        <PageHeader title="System Health" description="Live telemetry across the outbox, NetSuite gate, and API." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2 rounded-xl border p-4">
              <Skeleton className="h-3 w-24" /><Skeleton className="h-7 w-16" /><Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-2"><PanelSkeleton rows={4} /><PanelSkeleton rows={4} /></div>
        <PanelSkeleton rows={5} />
      </div>
    );
  }

  if (trendsError && !outboxTrends) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">{trendsError}</p>
        <Button onClick={() => { clearError(); fetchTrends(); }}>Try Again</Button>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto max-w-[1400px] space-y-8">
        <PageHeader
          title="System Health"
          description="Async work outbox, the NetSuite concurrency gate, and API traffic — live and over time."
          actions={
            <>
              {lastRefresh && (
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  Last updated: {new Date(lastRefresh).toLocaleTimeString()}
                </span>
              )}
              <Button variant="outline" size="sm" onClick={() => refresh()} disabled={isLoadingTrends}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoadingTrends ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </>
          }
        />

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={range} onValueChange={(v) => setRange(v as TimeRange)}>
            <TabsList>
              {RANGES.map((r) => (
                <TabsTrigger key={r} value={r}>{r}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />
            Live
          </span>
        </div>

        {/* Failed refresh / range-switch while data is already on screen — surface it instead of silently
            showing the stale, now-mislabeled load (the full-screen error above only covers the first load). */}
        {trendsError && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
            <span>Couldn't refresh — showing the last successful load.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                clearError();
                fetchTrends();
              }}
              disabled={isLoadingTrends}
            >
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isLoadingTrends ? 'animate-spin' : ''}`} />
              Retry
            </Button>
          </div>
        )}

        {/* 1 — Right now */}
        <Section label="Right now" caption="Outbox posting jobs (not API calls or gate waiters) — polled every few seconds.">
          <OutboxLiveTiles />
        </Section>

        {/* Document Capture (86d3rrfee) — link lane, review backlog, stations, reconciliation sweep. */}
        <Section label="Document Capture" caption="Scan-to-NetSuite pipeline health — link lane, review backlog, scan stations, and the nightly reconciliation sweep.">
          <DocumentCaptureHealthPanel />
        </Section>

        {/* 2 — NetSuite demand */}
        <Section label="NetSuite demand" caption={`Every DOP → NetSuite call, measured at the concurrency gate · ${rangeCaption}.`}>
          <GatePanel />
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartPanel
              title="NetSuite calls per day"
              description="Interactive (a user waited) vs background (workers)."
              chart={
                <StackedBarTrend
                  data={netsuiteTrends?.callsPerDay ?? []}
                  xKey="date"
                  xTickFormatter={fmtDate}
                  series={[
                    { key: 'interactive', name: 'Interactive', color: c.info },
                    { key: 'background', name: 'Background', color: c.muted },
                  ]}
                />
              }
              table={
                <SeriesTable
                  rows={netsuiteTrends?.callsPerDay ?? []}
                  maxHeightClass="max-h-60"
                  columns={[
                    { header: 'Date', cell: (r) => fmtDate(r.date) },
                    { header: 'Interactive', align: 'right', cell: (r) => r.interactive },
                    { header: 'Background', align: 'right', cell: (r) => r.background },
                  ]}
                />
              }
            />
            <ChartPanel
              title="Rejects & gate timeouts per day"
              description="NetSuite 429/503 rejects, gate acquire-timeouts, and transport errors."
              chart={
                <StackedBarTrend
                  data={rejectRows}
                  xKey="date"
                  xTickFormatter={fmtDate}
                  series={[
                    { key: 'rejects', name: 'NetSuite rejects', color: c.destructive },
                    { key: 'gateTimeouts', name: 'Gate timeout', color: c.warning },
                    { key: 'errors', name: 'Error', color: c.muted },
                  ]}
                />
              }
              table={
                <SeriesTable
                  rows={netsuiteTrends?.callsPerDay ?? []}
                  maxHeightClass="max-h-60"
                  columns={[
                    { header: 'Date', cell: (r) => fmtDate(r.date) },
                    { header: '429', align: 'right', cell: (r) => r.count429 },
                    { header: '503', align: 'right', cell: (r) => r.count503 },
                    { header: 'Gate t/o', align: 'right', cell: (r) => r.gateTimeouts },
                    { header: 'Errors', align: 'right', cell: (r) => r.errors },
                  ]}
                />
              }
            />
          </div>
          <ChartPanel
            title="NetSuite calls by origin"
            description="Which DOP endpoints drive the most NetSuite calls (the read-demand hammer)."
            chart={
              <HorizontalBarList
                data={netsuiteTrends?.topOrigins ?? []}
                categoryKey="originEndpoint"
                valueKey="count"
                color={c.info}
                height={Math.max(200, (netsuiteTrends?.topOrigins?.length ?? 0) * 30)}
                categoryWidth={200}
              />
            }
            table={
              <SeriesTable
                rows={netsuiteTrends?.topOrigins ?? []}
                maxHeightClass="max-h-72"
                columns={[
                  { header: 'Origin', cell: (r) => <code className="text-xs">{r.originEndpoint}</code> },
                  { header: 'Calls', align: 'right', cell: (r) => r.count },
                ]}
              />
            }
          />
        </Section>

        {/* 3 — Posting pipeline (outbox) */}
        <Section label="Posting pipeline (outbox)" caption={`Documents posting to NetSuite through the outbox · ${rangeCaption}.`}>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatKpiCard
              title="Success rate"
              titleTip="Share of terminal posting jobs that succeeded (Done ÷ Done+Failed). Deferrals are excluded — they aren't failures."
              value={`${outboxTrends?.successRatePct ?? 0}%`}
              subtitle={`Over ${outboxTrends?.terminalJobs ?? 0} finished jobs`}
              icon={CheckCircle2}
              tone="success"
            />
            <StatKpiCard
              title="Avg attempts per post"
              titleTip="Mean attempts over finished jobs — retry pressure. 1.0 is ideal; higher means jobs are retrying."
              value={outboxTrends?.avgAttempts?.toFixed(2) ?? '0'}
              subtitle="Retry pressure"
              icon={Repeat}
              tone="info"
            />
            <StatKpiCard
              title="Deferred (gate contention)"
              titleTip="Attempts backed off because the NetSuite gate was busy. Not counted as failures — capacity back-pressure."
              value={deferredTotal}
              subtitle="Not counted as failures"
              icon={PauseCircle}
              tone="warning"
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartPanel
              title="Posting attempts per day"
              description="Succeeded, deferred (gate contention), and failed attempts."
              chart={
                <StackedBarTrend
                  data={attemptRows}
                  xKey="date"
                  xTickFormatter={fmtDate}
                  series={[
                    { key: 'succeeded', name: 'Succeeded', color: c.success },
                    { key: 'deferred', name: 'Deferred', color: c.warning },
                    { key: 'failed', name: 'Failed', color: c.destructive },
                  ]}
                />
              }
              table={
                <SeriesTable
                  rows={outboxTrends?.attemptsPerDay ?? []}
                  maxHeightClass="max-h-60"
                  columns={[
                    { header: 'Date', cell: (r) => fmtDate(r.date) },
                    { header: 'OK', align: 'right', cell: (r) => r.succeeded },
                    { header: 'Deferred', align: 'right', cell: (r) => r.deferred },
                    { header: 'Transient', align: 'right', cell: (r) => r.failedTransient },
                    { header: 'Permanent', align: 'right', cell: (r) => r.failedPermanent },
                  ]}
                />
              }
            />
            <ChartPanel
              title="Posts by handler"
              description="Successful posts per outbox handler over the range."
              chart={
                <HorizontalBarList
                  data={handlerRows}
                  categoryKey="label"
                  valueKey="done"
                  color={c.info}
                  height={Math.max(200, handlerRows.length * 34)}
                  categoryWidth={110}
                />
              }
              table={
                <SeriesTable
                  rows={handlerRows}
                  maxHeightClass="max-h-60"
                  columns={[
                    { header: 'Handler', cell: (r) => r.label },
                    { header: 'Done', align: 'right', cell: (r) => r.done },
                    { header: 'Failed', align: 'right', cell: (r) => r.failed },
                    { header: 'In flight', align: 'right', cell: (r) => r.inFlight },
                  ]}
                />
              }
            />
          </div>
          <DeadLetterTable />
        </Section>

        {/* 4 — API traffic */}
        <Section label="API traffic" caption="DOP's own API — hourly aggregates by status class, no per-request rows (forensics live in Seq).">
          <div className="grid gap-4 sm:grid-cols-3">
            <StatKpiCard title="Requests" titleTip="Total inbound API requests over the range (from the hourly rollup)." value={httpTotals.total.toLocaleString()} subtitle={rangeCaption} icon={Activity} tone="info" />
            <StatKpiCard title="Error rate" titleTip="Share of requests that returned 5xx." value={`${httpTotals.errorRatePct.toFixed(1)}%`} subtitle="5xx share" icon={AlertTriangle} tone={httpTotals.errorRatePct > 1 ? 'destructive' : 'success'} valueClassName={httpTotals.errorRatePct > 1 ? 'text-destructive' : undefined} />
            <StatKpiCard title="Avg response" titleTip="Mean handler duration across the range (request-weighted)." value={`${Math.round(httpTotals.avgMs)} ms`} subtitle="Server-side latency" icon={Timer} tone="info" />
          </div>
          <ChartPanel
            title={range === '24h' ? 'Requests per hour' : 'Requests per day'}
            description={range === '90d' ? 'By status class · last 30 days (request-stats retention window).' : 'By status class (2xx / 4xx / 5xx).'}
            chart={
              <StackedBarTrend
                data={httpBuckets}
                xKey="label"
                series={[
                  { key: 'count2xx', name: '2xx', color: c.info },
                  { key: 'count4xx', name: '4xx', color: c.warning },
                  { key: 'count5xx', name: '5xx', color: c.destructive },
                ]}
              />
            }
            table={
              <SeriesTable
                rows={httpBuckets}
                maxHeightClass="max-h-64"
                columns={[
                  { header: range === '24h' ? 'Hour' : 'Date', cell: (r) => r.label },
                  { header: 'Total', align: 'right', cell: (r) => r.total },
                  { header: '2xx', align: 'right', cell: (r) => r.count2xx },
                  { header: '4xx', align: 'right', cell: (r) => r.count4xx },
                  { header: '5xx', align: 'right', cell: (r) => r.count5xx },
                  { header: 'Avg ms', align: 'right', cell: (r) => Math.round(r.avgDurationMs) },
                  { header: 'Max ms', align: 'right', cell: (r) => r.maxDurationMs },
                ]}
              />
            }
          />
        </Section>

        <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
          <Zap className="h-3.5 w-3.5" />
          Aggregates only — no per-request rows. Live gate state streams from memory; everything else is polled or queried from Postgres rollups.
        </div>
      </div>
    </TooltipProvider>
  );
}
